import { useNavigate } from "react-router-dom";
import { Header, Footer, API } from "../App";
import { useWidgetMode } from "@/lib/widgetMode";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, Check, Car, Palette, Settings, FileText, Download, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const VehicleConfirmScreen = ({ state, updateState }) => {
  const navigate = useNavigate();
  const { vehicleInfo } = state;
  const [downloadingSticker, setDownloadingSticker] = useState(false);
  const { isCompact: compact } = useWidgetMode();

  useEffect(() => {
    if (!vehicleInfo) {
      navigate('/');
    }
  }, [vehicleInfo, navigate]);

  if (!vehicleInfo) return null;

  const vehicleDetails = [
    { label: "Year", value: vehicleInfo.year, icon: Car },
    { label: "Make", value: vehicleInfo.make, icon: Car },
    { label: "Model", value: vehicleInfo.model, icon: Car },
    { label: "Trim", value: vehicleInfo.trim || "Base", icon: Settings },
    { label: "Body Type", value: vehicleInfo.body_type, icon: Car },
    { label: "Drive Type", value: vehicleInfo.drive_type, icon: Settings },
    { label: "Exterior Color", value: vehicleInfo.exterior_color, icon: Palette },
    { label: "Interior Color", value: vehicleInfo.interior_color, icon: Palette },
  ].filter(d => d.value);

  const formattedMSRP = vehicleInfo.msrp 
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(vehicleInfo.msrp)
    : null;

  const handleDownloadSticker = async () => {
    setDownloadingSticker(true);
    try {
      const res = await fetch(`${API}/vehicles/${state.vin}/window-sticker`);
      if (!res.ok) throw new Error("Failed to generate");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Window_Sticker_${vehicleInfo.year}_${vehicleInfo.make}_${vehicleInfo.model}.pdf`.replace(/ /g, "_");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Window sticker downloaded!");
    } catch {
      toast.error("Failed to generate window sticker");
    } finally {
      setDownloadingSticker(false);
    }
  };

  const fieldHeight = compact ? "h-11" : "h-14";

  return (
    <div className={compact ? "flex flex-col bg-white" : "min-h-screen flex flex-col bg-white"}>
      <Header showProgress currentStep={1} variant="light" />

      <main className={compact ? "py-4" : "min-h-[calc(100vh-80px)] py-12 sm:py-20"}>
        <div className="container-flow">
          {/* Header */}
          <div className={`text-center opacity-0 animate-fade-in-up ${compact ? "mb-4" : "mb-10"}`}>
            <span className={`text-label text-blue-600 block ${compact ? "mb-1" : "mb-4"}`}>STEP 2 OF 7</span>
            <h1 className={compact ? "text-xl font-medium tracking-tight text-slate-900 mb-1" : "text-heading-2 mb-4"}>
              Is This Your Vehicle?
            </h1>
            <p className={compact ? "text-sm text-slate-500" : "text-body"}>
              Please confirm the details below are correct for your {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}.
            </p>
          </div>

          {/* Actions */}
          <div className={`flex flex-col @sm:flex-row gap-4 opacity-0 animate-fade-in-up animate-stagger-2 ${compact ? "mb-4" : "mb-6"}`}>
            <Button
              onClick={() => navigate('/mileage-zip')}
              className={`flex-1 ${fieldHeight} btn-primary`}
              data-testid="confirm-vehicle-btn"
            >
              Yes
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className={`flex-1 ${fieldHeight} btn-secondary`}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              No
            </Button>
          </div>

          {/* Vehicle Card */}
          <div className={`surface-card opacity-0 animate-fade-in-up animate-stagger-3 ${compact ? "p-4" : "p-8 sm:p-10"}`}>
            {/* Vehicle Title */}
            <div className={`flex items-center justify-between border-b border-slate-200 ${compact ? "mb-4 pb-3" : "mb-8 pb-6"}`}>
              <div>
                <h2 className="text-heading-3">
                  {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}
                </h2>
                {vehicleInfo.trim && (
                  <p className="text-slate-600">{vehicleInfo.trim}</p>
                )}
              </div>
              {formattedMSRP && (
                <div className="text-right">
                  <p className="text-label mb-1">ORIGINAL MSRP</p>
                  <p className="text-xl font-semibold text-slate-900">{formattedMSRP}</p>
                </div>
              )}
            </div>

            {/* VIN */}
            <div className={`bg-slate-50 rounded-xl p-4 ${compact ? "mb-4" : "mb-6"}`}>
              <p className="text-label mb-1">VIN</p>
              <p className="font-mono text-lg tracking-wider text-slate-900" data-testid="confirmed-vin">
                {state.vin}
              </p>
            </div>

            {/* Details Grid */}
            <div className={`grid grid-cols-2 gap-4 ${compact ? "mb-4" : "mb-8"}`}>
              {vehicleDetails.map((detail, index) => (
                <div key={detail.label} className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <detail.icon className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">{detail.label}</p>
                    <p className="font-medium text-slate-900">{detail.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Options if any */}
            {vehicleInfo.options && vehicleInfo.options.length > 0 && (
              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-label mb-3">INSTALLED OPTIONS</p>
                <div className="flex flex-wrap gap-2">
                  {vehicleInfo.options.slice(0, 8).map((opt) => (
                    <span 
                      key={opt.code || opt.description}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 rounded-lg text-sm text-slate-700"
                    >
                      <Check className="w-3 h-3 text-emerald-600" />
                      {opt.description || opt.code}
                    </span>
                  ))}
                  {vehicleInfo.options.length > 8 && (
                    <span className="px-3 py-1.5 text-sm text-slate-500">
                      +{vehicleInfo.options.length - 8} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Window Sticker CTA */}
          <div className={`opacity-0 animate-fade-in-up animate-stagger-4 ${compact ? "mt-3" : "mt-5"}`}>
            <button
              onClick={handleDownloadSticker}
              disabled={downloadingSticker}
              className={`w-full group relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white transition-all hover:shadow-lg hover:border-slate-300 ${compact ? "p-3" : "p-5"}`}
              data-testid="download-window-sticker-btn"
            >
              {/* Accent line */}
              <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, hsl(220, 70%, 15%) 0%, hsl(48, 96%, 53%) 100%)' }} />
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'hsl(220, 70%, 15%)' }}>
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-semibold text-slate-900">Free OEM Window Sticker</span>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full text-accent-yellow" style={{ backgroundColor: 'hsl(220, 70%, 15%)' }}>
                      FREE
                    </span>
                  </div>
                  <p className="text-sm text-slate-500">
                    Full specs, options, safety features &amp; MSRP — decoded directly from your VIN
                  </p>
                </div>
                <div className="flex-shrink-0">
                  {downloadingSticker ? (
                    <div className="w-10 h-10 flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                      <Download className="w-5 h-5 text-slate-600" />
                    </div>
                  )}
                </div>
              </div>
            </button>
          </div>
        </div>
      </main>
      {!compact && <Footer minimal />}
    </div>
  );
};

export default VehicleConfirmScreen;
